import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MessageSquare, Pencil, Send, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSite } from "../lib/site";
import SmartImage from "../components/SmartImage";
import { useHoneypot } from "../components/Honeypot";
import { MarketingOptIn, OPT_IN_NEEDS_EMAIL } from "../components/MarketingOptIn";
import useSeo from "../hooks/useSeo";
import { trackEnquiryConversion } from "../lib/analytics";

/**
 * Context-aware enquiry. Every Enquire Now button passes what the visitor
 * clicked (product / catalogue item / category page) so the form arrives
 * pre-filled with a summary card instead of being a blank general enquiry.
 */
export default function Enquire() {
  const [params] = useSearchParams();
  const { site } = useSite();

  const refType = params.get("refType") || "";
  const refId = params.get("refId") || "";

  const [ctx, setCtx] = useState(() => ({
    ref_type: refType,
    ref_id: refId,
    ref_slug: "",
    title: params.get("refTitle") || "",
    image: params.get("refImage") || "",
    price: params.get("refPrice") || "",
    category: params.get("refCategory") || "",
    brand: params.get("refBrand") || "",
    description: "",
    back_url: params.get("refUrl") || "",
  }));

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: params.get("refVehicle") || "",
    rego: "",
    message: "",
  });
  const { honeypot, HoneypotField } = useHoneypot();
  const [optIn, setOptIn] = useState(false);
  useSeo({ title: "Enquire - NQ Springs & 4x4 Accessories", noindex: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  /* enrich the summary card straight from the database when we have an id */
  useEffect(() => {
    if (!refType || !refId) return;
    api
      .get("/enquiry-context", { params: { ref_type: refType, ref_id: refId } })
      .then((r) => setCtx((c) => ({ ...c, ...r.data })))
      .catch(() => {});
  }, [refType, refId]);

  /* seed the message with what they're enquiring about */
  useEffect(() => {
    if (!ctx.title) return;
    setForm((f) => {
      if (f.message) return f;
      const brand = ctx.brand ? ` (${ctx.brand})` : "";
      return {
        ...f,
        message:
          `Hi NQ Springs, I'd like more information about "${ctx.title}"${brand}. ` +
          `Could you please confirm pricing, fitment and availability for my vehicle?`,
      };
    });
  }, [ctx.title, ctx.brand]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    // Owner's rule (round 4): they look the vehicle up from the rego, so name +
    // phone + rego are what they actually need. Email is OPTIONAL.
    if (!form.name.trim() || !form.phone.trim() || !form.rego.trim()) {
      toast.error("Please fill in your name, phone number and rego");
      return;
    }
    if (optIn && !form.email.trim()) {
      toast.error(OPT_IN_NEEDS_EMAIL);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        rego: form.rego,
        website: honeypot,
        marketing_opt_in: optIn,
        vehicle: form.vehicle || "",
        message:
          form.message.trim() ||
          `Enquiry from ${form.name} regarding rego ${form.rego}` +
            (ctx.title ? ` - interested in ${ctx.title}` : ""),
        ref_type: ctx.ref_type || "general",
        ref_id: ctx.ref_id || "",
        ref_slug: ctx.ref_slug || "",
        ref_title: ctx.title || "",
        ref_image: ctx.image || "",
        ref_category: ctx.category || "",
        ref_brand: ctx.brand || "",
        ref_price: ctx.price || "",
        ref_url: ctx.back_url || "",
      };
      // Only include email when supplied - posting "" fails EmailStr validation.
      if (form.email.trim()) {
        payload.email = form.email;
      }
      const { data } = await api.post("/enquiries", payload);
      setDone(data.enquiry.reference);
      trackEnquiryConversion({ source: "enquiry_page", email: form.email, phone: form.phone });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let errorMsg = "Please try again.";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map((d) => d.msg || d.message || "Validation error").join(", ");
      }
      toast.error("Could not send enquiry", { description: errorMsg });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="container-nq section-nq">
        <div className="card-nq mx-auto max-w-xl p-10 text-center" data-testid="enquiry-success">
          <CheckCircle2 className="mx-auto h-12 w-12 gold-text" />
          <h1 className="mt-4 text-2xl font-bold">Enquiry sent</h1>
          <p className="mt-3 text-sm text-[color:var(--nq-stone)]">
            Your reference is <span className="font-mono gold-text">{done}</span>.
            {ctx.title ? ` We have noted your interest in ${ctx.title}.` : ""} Our team will be in
            touch shortly.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/" className="btn-gold">
              Back home
            </Link>
            <Link to="/shop" className="btn-outline-nq">
              Browse surplus stock
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasContext = Boolean(ctx.title);
  const field =
    "w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-[color:var(--nq-gold)]";
  const labelCls =
    "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]";
  const bd = { borderColor: "rgba(255,255,255,0.14)" };

  return (
    <div className="container-nq section-nq" data-testid="enquire-page">
      <div className="mx-auto max-w-5xl">
        <p className="eyebrow">Enquiry</p>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">
          {hasContext ? "Enquire about this product" : "Send us an enquiry"}
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-[color:var(--nq-stone)]">
          Give us your vehicle details and we&apos;ll match the right setup, confirm fitment and get you
          a price.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-12">
          {hasContext && (
            <aside className="lg:col-span-5">
              <div
                className="sticky top-28 rounded-xl border p-5"
                style={{
                  borderColor: "rgba(198,162,74,0.35)",
                  background: "rgba(198,162,74,0.05)",
                }}
                data-testid="enquiry-summary-card"
              >
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: "var(--nq-gold)", color: "var(--nq-black)" }}
                >
                  <Tag className="h-3 w-3" /> You are enquiring about
                </span>

                <div className="mt-4 overflow-hidden rounded-lg">
                  <SmartImage
                    src={ctx.image}
                    alt={ctx.title}
                    ratio="aspect-[4/3]"
                    fit="contain"
                    fallbackLabel={ctx.brand || ctx.title}
                  />
                </div>

                <h2 className="mt-4 text-lg font-semibold" data-testid="enquiry-summary-title">
                  {ctx.title}
                </h2>

                <dl className="mt-3 space-y-1.5 text-xs">
                  {ctx.brand && (
                    <div className="flex gap-2">
                      <dt className="w-20 shrink-0 text-[color:var(--nq-stone)]">Brand</dt>
                      <dd className="font-medium">{ctx.brand}</dd>
                    </div>
                  )}
                  {ctx.category && (
                    <div className="flex gap-2">
                      <dt className="w-20 shrink-0 text-[color:var(--nq-stone)]">Category</dt>
                      <dd className="font-medium">{ctx.category}</dd>
                    </div>
                  )}
                  {ctx.price && (
                    <div className="flex gap-2">
                      <dt className="w-20 shrink-0 text-[color:var(--nq-stone)]">Price</dt>
                      <dd className="font-mono font-semibold gold-text">{ctx.price}</dd>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 text-[color:var(--nq-stone)]">Reference</dt>
                    <dd className="font-mono text-[color:var(--nq-stone)]">
                      {ctx.ref_type}:{String(ctx.ref_id).slice(0, 12)}
                    </dd>
                  </div>
                </dl>

                {ctx.description && (
                  <p className="mt-3 line-clamp-4 text-xs leading-relaxed text-[color:var(--nq-stone)]">
                    {ctx.description}
                  </p>
                )}

                {ctx.back_url && (
                  <Link
                    to={ctx.back_url}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold gold-text hover:underline"
                    data-testid="enquiry-edit-selection"
                  >
                    <Pencil className="h-3 w-3" /> Change selection
                  </Link>
                )}
              </div>
            </aside>
          )}

          <div className={hasContext ? "lg:col-span-7" : "lg:col-span-8"}>
            <form
              className="card-nq grid gap-4 p-6 sm:grid-cols-2 md:p-8"
              onSubmit={submit}
              data-testid="enquiry-form"
            >
              {HoneypotField}
              <div>
                <label className={labelCls}>Your name *</label>
                <input value={form.name} onChange={set("name")} required className={field} style={bd} data-testid="enquiry-name-input" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" value={form.email} onChange={set("email")} className={field} style={bd} data-testid="enquiry-email-input" />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input value={form.phone} onChange={set("phone")} required inputMode="tel" className={field} style={bd} data-testid="enquiry-phone-input" />
              </div>
              <div>
                <label className={labelCls}>Vehicle</label>
                <input
                  value={form.vehicle}
                  onChange={set("vehicle")}
                  placeholder="e.g. 2021 Toyota 79 Series"
                  className={field}
                  style={bd}
                  data-testid="enquiry-vehicle-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Rego number *</label>
                <input
                  value={form.rego}
                  onChange={set("rego")}
                  required
                  placeholder="We'll look up your vehicle from this"
                  className={field}
                  style={bd}
                  data-testid="enquiry-rego-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Your message</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className={field}
                  style={bd}
                  data-testid="enquiry-message-input"
                />
                {hasContext && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[color:var(--nq-stone)]">
                    <MessageSquare className="h-3 w-3 gold-text" />
                    Pre-filled from the product you selected - edit it however you like.
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <MarketingOptIn id="enquire-marketing-opt-in" checked={optIn} onChange={setOptIn} />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-gold w-full px-6 py-3 disabled:opacity-60"
                  data-testid="enquiry-form-submit-button"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {busy ? "Sending..." : "Send enquiry"}
                </button>
                <p className="mt-3 text-center text-xs text-[color:var(--nq-stone)]">
                  Or call us on{" "}
                  <a
                    href={`tel:${(site?.contact?.phone || "").replace(/[^\d+]/g, "")}`}
                    className="gold-text"
                  >
                    {site?.contact?.phone}
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
